import { Routes } from '@angular/router';
import { autoLoginPartialRoutesGuard } from 'angular-auth-oidc-client';
import type { AppRole } from '@core/api/me';
import { roleGuard } from '@core/auth/role.guard';
import { Shell } from '@layout/shell/shell/shell';

/** Роли разделов: один список и для roleGuard, и для меню (data.roles). */
const EDITOR_ROLES: readonly AppRole[] = ['administrator', 'expert', 'manager'];
const ADMIN_ROLES: readonly AppRole[] = ['administrator'];

/**
 * Маршруты. Всё под Shell требует входа (autoLoginPartialRoutesGuard уводит на Keycloak)
 * и назначенной роли (roleGuard). Разделы — заглушки до задач фронтенда.
 *
 * Раздел с кнопкой «Создать» — контейнер без компонента: список лежит на пути '',
 * страница создания — на пути 'create'. Подпись кнопки (data.nav.create) живёт
 * на маршруте списка: данные маршрута наследуются родителем к ребёнку, иначе
 * страница создания унаследовала бы подпись и кнопка вела бы сама на себя.
 * `title` маршрута — заголовок документа, `data.title` — заголовок на странице.
 */
export const routes: Routes = [
    {
        path: 'frontchannel-logout',
        loadComponent: () =>
            import('./pages/system/frontchannel-logout').then((m) => m.FrontchannelLogout),
    },
    {
        path: 'unauthorized',
        loadComponent: () => import('./pages/system/unauthorized').then((m) => m.Unauthorized),
    },
    {
        path: 'forbidden',
        loadComponent: () => import('./pages/system/forbidden').then((m) => m.Forbidden),
    },
    {
        path: 'error',
        loadComponent: () => import('./pages/system/load-error').then((m) => m.LoadError),
    },
    {
        path: 'no-role',
        canActivate: [autoLoginPartialRoutesGuard],
        loadComponent: () => import('./pages/system/no-role').then((m) => m.NoRole),
    },
    {
        path: '',
        component: Shell,
        canActivate: [autoLoginPartialRoutesGuard, roleGuard()],
        children: [
            { path: '', pathMatch: 'full', redirectTo: 'evaluations' },
            {
                path: 'evaluations',
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        title: 'Реестр оценок — НОК',
                        data: {
                            nav: {
                                section: null,
                                label: 'Реестр оценок',
                                icon: 'registry',
                                primary: true,
                                order: 10,
                                create: 'Создать оценку',
                            },
                        },
                        loadComponent: () =>
                            import('./pages/evaluations/evaluations').then((m) => m.Evaluations),
                    },
                    {
                        path: 'create',
                        title: 'Создание оценки — НОК',
                        data: {
                            title: 'Создание оценки',
                            task: 'F-05, F-06 — реестр и карточка оценки (nok.docs/tasks/frontend.md)',
                        },
                        loadComponent: () =>
                            import('./pages/evaluations/evaluations').then((m) => m.Evaluations),
                    },
                ],
            },
            {
                path: 'organizations',
                canActivate: [roleGuard()],
                // Ключ справочника: страница переиспользуется между своими маршрутами
                // (список, create, :id), но не смешивается с другими справочниками.
                data: { roles: EDITOR_ROLES, dictionary: 'organizations' },
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        title: 'Справочники — Учреждения МКС',
                        data: {
                            nav: {
                                section: 'Справочники',
                                label: 'Учреждения МКС',
                                icon: 'organizations',
                                order: 20,
                                create: 'Создать учреждение',
                            },
                        },
                        loadComponent: () =>
                            import('./pages/references/organizations/organizations').then(
                                (m) => m.Organizations,
                            ),
                    },
                    {
                        path: 'create',
                        title: 'Создание учреждения — НОК',
                        data: {
                            title: 'Создание учреждения',
                            task: 'F-03, F-04 — справочники методики и учреждений',
                        },
                        loadComponent: () =>
                            import('./pages/references/organizations/organizations').then(
                                (m) => m.Organizations,
                            ),
                    },
                    {
                        path: ':id',
                        title: 'Редактирование учреждения — НОК',
                        data: {
                            title: 'Редактирование учреждения',
                            task: 'F-03, F-04 — справочники методики и учреждений',
                        },
                        loadComponent: () =>
                            import('./pages/references/organizations/organizations').then(
                                (m) => m.Organizations,
                            ),
                    },
                ],
            },
            {
                path: 'groups',
                canActivate: [roleGuard()],
                // Ключ справочника: страница переиспользуется между своими маршрутами
                // (список, create, :id), но не смешивается с другими справочниками.
                data: { roles: EDITOR_ROLES, dictionary: 'groups' },
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        title: 'Справочники — Группы учреждений МКС',
                        data: {
                            nav: {
                                section: 'Справочники',
                                label: 'Группы учреждений МКС',
                                icon: 'groups',
                                order: 30,
                                create: 'Создать группу учреждений',
                            },
                        },
                        loadComponent: () =>
                            import('./pages/references/organization-groups/organization-groups').then(
                                (m) => m.OrganizationGroups,
                            ),
                    },
                    {
                        path: 'create',
                        title: 'Создание Группы учреждений — НОК',
                        data: { title: 'Создание Группы учреждений' },
                        loadComponent: () =>
                            import('./pages/references/organization-groups/organization-groups').then(
                                (m) => m.OrganizationGroups,
                            ),
                    },
                    {
                        path: ':id',
                        title: 'Редактирование Группы учреждений — НОК',
                        data: { title: 'Редактирование Группы учреждений' },
                        loadComponent: () =>
                            import('./pages/references/organization-groups/organization-groups').then(
                                (m) => m.OrganizationGroups,
                            ),
                    },
                ],
            },
            {
                path: 'criteria',
                canActivate: [roleGuard()],
                // Ключ справочника: страница переиспользуется между своими маршрутами
                // (список, create, :id), но не смешивается с другими справочниками.
                data: { roles: EDITOR_ROLES, dictionary: 'criteria' },
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        title: 'Справочники — Критерии',
                        data: {
                            nav: {
                                section: 'Справочники',
                                label: 'Критерии',
                                icon: 'criteria',
                                order: 40,
                                create: 'Создать критерий',
                            },
                        },
                        loadComponent: () =>
                            import('./pages/references/criteria/criteria').then((m) => m.Criteria),
                    },
                    {
                        path: 'create',
                        title: 'Создание критерия — НОК',
                        data: {
                            title: 'Создание критерия',
                            task: 'F-03, F-04 — справочники методики и учреждений',
                        },
                        loadComponent: () =>
                            import('./pages/references/criteria/criteria').then((m) => m.Criteria),
                    },
                    {
                        path: ':id',
                        title: 'Редактирование критерия — НОК',
                        data: {
                            title: 'Редактирование критерия',
                            task: 'F-03, F-04 — справочники методики и учреждений',
                        },
                        loadComponent: () =>
                            import('./pages/references/criteria/criteria').then((m) => m.Criteria),
                    },
                ],
            },
            {
                path: 'indicators',
                canActivate: [roleGuard()],
                data: { roles: EDITOR_ROLES },
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        title: 'Справочники — Показатели',
                        data: {
                            nav: {
                                section: 'Справочники',
                                label: 'Показатели',
                                icon: 'indicators',
                                order: 50,
                                create: 'Создать показатель',
                            },
                        },
                        loadComponent: () =>
                            import('./pages/references/references').then((m) => m.References),
                    },
                    {
                        path: 'create',
                        title: 'Создание показателя — НОК',
                        data: {
                            title: 'Создание показателя',
                            task: 'F-03, F-04 — справочники методики и учреждений',
                        },
                        loadComponent: () =>
                            import('./pages/system/placeholder').then((m) => m.Placeholder),
                    },
                    {
                        path: ':id',
                        title: 'Редактирование показателя — НОК',
                        data: {
                            title: 'Редактирование показателя',
                            task: 'F-03, F-04 — справочники методики и учреждений',
                        },
                        loadComponent: () =>
                            import('./pages/system/placeholder').then((m) => m.Placeholder),
                    },
                ],
            },
            {
                path: 'indexes',
                canActivate: [roleGuard()],
                data: { roles: EDITOR_ROLES },
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        title: 'Справочники — Индексы',
                        data: {
                            nav: {
                                section: 'Справочники',
                                label: 'Индексы',
                                icon: 'indexes',
                                order: 60,
                                create: 'Создать индекс',
                            },
                        },
                        loadComponent: () =>
                            import('./pages/references/references').then((m) => m.References),
                    },
                    {
                        path: 'create',
                        title: 'Создание индекса — НОК',
                        data: {
                            title: 'Создание индекса',
                            task: 'F-03, F-04 — справочники методики и учреждений',
                        },
                        loadComponent: () =>
                            import('./pages/system/placeholder').then((m) => m.Placeholder),
                    },
                    {
                        path: ':id',
                        title: 'Редактирование индекса — НОК',
                        data: {
                            title: 'Редактирование индекса',
                            task: 'F-03, F-04 — справочники методики и учреждений',
                        },
                        loadComponent: () =>
                            import('./pages/system/placeholder').then((m) => m.Placeholder),
                    },
                ],
            },
            {
                path: 'services',
                canActivate: [roleGuard()],
                data: { roles: EDITOR_ROLES },
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        title: 'Справочники — Сервисы анкетирования',
                        data: {
                            nav: {
                                section: 'Справочники',
                                label: 'Сервисы анкетирования',
                                icon: 'services',
                                order: 60,
                                create: 'Создать сервис',
                            },
                        },
                        loadComponent: () =>
                            import('./pages/references/references').then((m) => m.References),
                    },
                    {
                        path: 'create',
                        title: 'Создание сервиса анкетирования — НОК',
                        data: {
                            title: 'Создание сервиса анкетирования',
                            task: 'F-03, F-04 — справочники методики и учреждений',
                        },
                        loadComponent: () =>
                            import('./pages/system/placeholder').then((m) => m.Placeholder),
                    },
                    {
                        path: ':id',
                        title: 'Редактирование сервиса анкетирования — НОК',
                        data: {
                            title: 'Редактирование сервиса анкетирования',
                            task: 'F-03, F-04 — справочники методики и учреждений',
                        },
                        loadComponent: () =>
                            import('./pages/system/placeholder').then((m) => m.Placeholder),
                    },
                ],
            },
            {
                path: 'analytics',
                title: 'Аналитика — НОК',
                canActivate: [roleGuard()],
                data: {
                    nav: {
                        section: 'Аналитика и отчетность',
                        label: 'Аналитика',
                        icon: 'analytics',
                        order: 30,
                    },
                    roles: EDITOR_ROLES,
                },
                loadComponent: () => import('./pages/analytics/analytics').then((m) => m.Analytics),
            },
            {
                path: 'admin',
                canActivate: [roleGuard()],
                data: {
                    nav: {
                        section: 'Администрирование',
                        label: 'Администрирование',
                        icon: 'users',
                        order: 40,
                    },
                    roles: ADMIN_ROLES,
                },
                children: [
                    {
                        path: 'users',
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                title: 'Пользователи — НОК',
                                data: {
                                    nav: {
                                        label: 'Пользователи',
                                        icon: 'users',
                                        create: 'Создать пользователя',
                                    },
                                },
                                loadComponent: () =>
                                    import('./pages/admin/users').then((m) => m.Users),
                            },
                            {
                                path: 'create',
                                title: 'Создание пользователя — НОК',
                                data: {
                                    title: 'Создание пользователя',
                                    task: 'F-07 — роли и учреждения пользователей, блокировка',
                                },
                                loadComponent: () =>
                                    import('./pages/system/placeholder').then((m) => m.Placeholder),
                            },
                        ],
                    },
                ],
            },
        ],
    },
    { path: '**', redirectTo: '' },
];
